import path from 'path';

import { AxiosResponse } from 'axios';

import { IdReference, OffsetPagedResults } from '~interfaces';
import { SearchPagedParameters } from '~interfaces/requests';
import {
	BaseReferenceResponse,
	IdReferenceResponse,
	SkcOffsetPagedResponse,
	SkcSingleResponse,
} from '~interfaces/responses';
import { TopologyProxiedSkopeiConnectService } from '~services';

import SkcUserGroup, { SkcUserGroupNew } from '../interfaces/skcUserGroup';

// Delete me, because of merge conflict

/**
 * A service that does calls to the Skopei Connect API through
 * the Topology backend
 */
class SkcUserGroupsService extends TopologyProxiedSkopeiConnectService {
	public readonly path = 'key-groups';

	public readonly keysSubPath = 'keys';

	async getUserGroups({
		page = 1,
		pageSize = 10,
		...args
	}: SearchPagedParameters): Promise<OffsetPagedResults<SkcUserGroup>> {
		const { data } = await this._client.get<SkcOffsetPagedResponse<UserGroupResponse>>(this.path, {
			params: {
				'page-number': page,
				'page-size': pageSize,
				'organisation-id': args.organisationId,
				'search-query': args.searchQuery || undefined,
			},
		});

		return this.mapOffsetPagedResponse(data, SkcUserGroupsService.fromResponse);
	}

	async getUserGroupById(id: string): Promise<SkcUserGroup> {
		const { data } = await this._client.get<SkcSingleResponse<UserGroupResponse>>(
			path.join(this.path, id)
		);

		return SkcUserGroupsService.fromResponse(data.data);
	}

	/**
	 * Create the most basic form of user group in Skopei Connect.
	 * Is not assigning users or tags!
	 * @param data
	 * @returns
	 */
	async createUserGroup(data: SkcUserGroup): Promise<IdReference> {
		const content = SkcUserGroupsService.toRequest(data);

		const response = await this._client.post<
			null,
			AxiosResponse<SkcSingleResponse<IdReferenceResponse>>,
			SkcUserGroupRequest
		>(this.path, content, {
			params: {
				'organisation-id': data.organisation.id,
			},
		});

		return this.mapIdResponse(response.data);
	}

	/**
	 * Use this function to not only create a user group, but also assign users and/or nfc tags
	 * @param data
	 * @returns
	 */
	async createCompleteUserGroup(data: SkcUserGroup): Promise<null> {
		const response = await this.createUserGroup(data);

		await this.updateParticipantsOfUserGroup(response.id, data);

		return null;
	}

	/**
	 * Update the name and description of the user group
	 * @param groupId
	 * @param data
	 */
	async updateUserGroup(groupId: string, data: SkcUserGroupNew): Promise<null> {
		const content = SkcUserGroupsService.toRequest(data);
		await this._client.put(path.join(this.path, groupId), content);

		return null;
	}

	/**
	 * Updating the group details, assigning and deleting users and nfc are all together
	 * five different calls. Combine it here
	 * @param groupId
	 * @param newData
	 * @param oldData
	 */
	async updateCompleteUserGroup(groupId: string, newData: SkcUserGroupNew): Promise<null> {
		await this.updateUserGroup(groupId, newData);
		await this.updateParticipantsOfUserGroup(groupId, newData);

		return null;
	}

	async updateParticipantsOfUserGroup(groupId: string, data: SkcUserGroup): Promise<void> {
		const userIds = data.participants.users?.map((el) => el.id);
		const nfcTagIds = data.participants.nfcTags?.map((el) => el.id);
		await this._client.put<AxiosResponse<UserGroupResponse>>(
			path.join(this.path, groupId, this.keysSubPath),
			{
				users: userIds,
				nfcTags: nfcTagIds,
			}
		);
	}

	async deleteUserGroup(id: string): Promise<null> {
		await this._client.delete(path.join(this.path, id));

		return null;
	}

	static toRequest(data: SkcUserGroupNew): SkcUserGroupRequest {
		const { label, description } = data;

		return {
			name: label,
			// Prevent empty strings
			description: description || undefined,
		};
	}

	static fromResponse(data: UserGroupResponse): SkcUserGroup {
		const { id, name, keys, organisation, ...rest } = data;

		return {
			...rest,
			id: id.toString(),
			label: name,
			participants: {
				nfcTags:
					keys
						.filter((el) => !el.virtualKeyEnabled)
						.map((el) => ({
							...el,
							id: el.skcId?.toString(),
							label: el.name,
							tagNumber: el.serialNumber,
						})) ?? [],
				users:
					keys
						.filter((el) => el.virtualKeyEnabled)
						.map((el) => ({
							...el,
							id: el.id.toString(),
							label: el.name,
						})) ?? [],
			},
			keys: keys.map((el) => ({
				...el,
				type:
					el.encryptionEnabled ? 'secureTag'
					: !el.virtualKeyEnabled ? 'externalTag'
					: 'user',
				id: el.id?.toString() ?? el.skcId?.toString(),
				label: el.name,
			})),
			organisation: SkcUserGroupsService.fromBaseReferenceResponse(organisation),
		};
	}
}

interface UserGroupResponse extends SkcUserGroupRequest {
	id: number;
	keys: {
		name: string;
		id?: number;
		encryptionEnabled: boolean;
		virtualKeyEnabled: boolean;
		skcId: number;
	}[];
	organisation: BaseReferenceResponse;
}

interface SkcUserGroupRequest {
	name: string;
	description?: string;
}

interface SkcUserGroupParticipantsRequest {
	userIds: number[];
	nfcTagIds: string[];
}

export default SkcUserGroupsService;
